import { Cancel, Delete, Edit, Padding, Save, Send } from "@mui/icons-material";
import { Avatar, Box, Button, CircularProgress, IconButton, Paper } from "@mui/material";
import { format } from "date-fns";
import Picker from "emoji-picker-react";
import { observer } from "mobx-react";
import React, { useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { ActivityQueryFiltersDtoTypeEnum, TicketDto } from "../../../api-client";
import TextArea from "../../../atoms/TextArea";
import { MobiliAvatar } from "../../../atoms/MobiliAvatar";
import useStore from "../../../helpers/useStore";
import {
  useAddComment,
  useCreateTicket,
  useDeleteComment,
  useFetchTicketActivities,
} from "../../../stores/Ticketing";
import { UserContext } from "../../../stores/User/User.provider";
import CommentInput from "./CommentInput";

const TicketComments: React.FC<{
  selectedTicket: TicketDto;
  setTicketData: (ticketData: TicketDto) => void;
}> = observer(({ selectedTicket, setTicketData }) => {
  const UserStore = useStore(UserContext);
  const { user } = UserStore;
  const ticketId = selectedTicket.id;
  const [newCommentText, setNewCommentText] = useState("");
  // const [editableCommentId, setEditableCommentId] = useState<number | null>(null);
  const [editableCommentId, setEditableCommentId] = useState<string | null>(null);
  const [editedCommentText, setEditedCommentText] = useState<string>("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const addTicket = useCreateTicket();
  const addComment = useAddComment();
  const deleteComment = useDeleteComment();
  const {
    data: commentActivities,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchTicketActivities({
    limit: 10,
    type: [ActivityQueryFiltersDtoTypeEnum.Comments],
    ticketId: ticketId,
  });

  // useEffect(() => {
  //   setComments(selectedTicket);
  // }, [selectedTicket]);

  const handleEmojiSelect = (emoji: any) => {
    if (emoji && emoji.emoji) {
      setNewCommentText((prevText) => prevText + emoji.emoji);
    }
    setShowEmojiPicker(false);
  };

  // Function to handle submitting a new comment
  const handleSubmit = async (data: any) => {
    try {
      let currentTicketId = ticketId;
      // If no ticketId, create the ticket first
      if (!currentTicketId) {
        const response = await addTicket.mutateAsync(selectedTicket); //.

        currentTicketId = response.id; // Update currentTicketId with the newly created ticket ID
        setTicketData(response);
      }

      let response = await addComment.mutateAsync({
        ticketId: currentTicketId!,
        commentData: { comment: newCommentText },
      });
      if (response) {
        setNewCommentText("");
      }
    } catch (error) {
      console.error("Error submitting comment:", error);
    }
  };

  // Function to handle editing a comment
  const handleEdit = (comment: any) => {
    // console.log("comment", comment);
    setEditableCommentId(comment.id);
    setEditedCommentText(comment.text?.current);
    // You can implement further logic for editing the comment here
  };

  // Function to handle canceling edit mode
  const handleCancelEdit = () => {
    setEditableCommentId(null);
    setEditedCommentText("");
  };

  // Function to handle saving the edited comment
  const handleSaveEdit = async (commentData: any, updatedCommentText: string) => {
    try {
      if (!ticketId) return;
      // Call the API to update the comment
      const response = await addComment.mutateAsync({
        ticketId,
        commentData: { activityId: commentData.id, comment: updatedCommentText },
      });
      if (response) {
        setEditableCommentId(null);
        setEditedCommentText("");
      }
    } catch (error) {
      console.error("Error updating comment:", error);
    }
  };

  const handleDelete = async (ticketId: number | undefined, activityId: string) => {
    try {
      let response = await deleteComment.mutateAsync({ ticketId, activityId });
      // if (response) {
      //   setComments(response);
      //   setTicketData(response);
      // }
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  const fetchedCommentActivitiesCount =
    commentActivities?.pages.reduce((total, page) => total + page.length, 0) || 0;

  return (
    <Box sx={{ 
      height: '100%', 
      width: '100%',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Scrollable Comments Area */}
      <Box
        id="scrollableDiv"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: '90px', // Reserve exactly 90px for input
          overflowY: "auto",
          overflowX: "hidden",
          paddingBottom: 1,
        }}
      >
        <InfiniteScroll
          dataLength={fetchedCommentActivitiesCount}
          next={() => {
            fetchNextPage();
          }}
          hasMore={!!hasNextPage}
          loader={
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} color="inherit" />
            </Box>
          }
          scrollableTarget="scrollableDiv"
        >
          {commentActivities?.pages.map((page) =>
            page.map((activity, index: any) => {
              // Check if this comment is by the logged-in user
              const isOwnComment = activity.text?.updatedByUser?.id === user?.id;
              
              return (
              <React.Fragment key={activity.id}>
                <Box
                  key={activity.id}
                  sx={{
                    display: "flex",
                    justifyContent: isOwnComment ? "flex-end" : "flex-start",
                    padding: "3px",
                    minWidth: "50%",
                    minHeight: "50%",
                    alignItems: "flex-start",
                  }}
                >
                  {!isOwnComment && (
                    <Box sx={{ paddingRight: 2 }}>
                      <MobiliAvatar
                        user={activity.text?.updatedByUser}
                        sx={{ width: 32, height: 32, fontSize: "0.8rem" }}
                      />
                    </Box>
                  )}
                  
                  <Paper
                    key={activity.id}
                    elevation={3}
                    sx={{
                      padding: "10px",
                      position: "relative",
                      background: isOwnComment ? "#5d8aa8" : "white",
                      marginBottom: "5px",
                      width: "65%", // Slightly narrower
                      maxWidth: "400px", // Max width limit
                      minHeight: "60px", // Slightly taller
                      display: "flex",
                      alignItems: "flex-start",
                      borderRadius: "10px",
                      boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    {editableCommentId === activity.id ? (
                      <TextArea
                        value={editedCommentText}
                        onChange={(e: any) => setEditedCommentText(e.target.value)}
                        sx={{ wordWrap: "break-word", flexDirection: "column" }}
                          placeholder="Bearbeite deinen Kommentar hier..."
                      />
                    ) : (
                      <div
                        style={{
                          width: "70%",
                          wordWrap: "break-word",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <div style={{ 
                          fontSize: "14px", 
                          fontFamily: "sans-serif",
                          color: isOwnComment ? "white" : "black"
                        }}>
                          {activity.text?.current}
                        </div>
                        <div style={{ 
                          fontSize: "12px", 
                          color: isOwnComment ? "rgba(255, 255, 255, 0.8)" : "#888", 
                          paddingTop: "10px" 
                        }}>
                          {activity?.createdAt &&
                            format(new Date(activity.createdAt), "dd.MM.yyyy, HH:mm")}
                        </div>
                      </div>
                    )}
                    {/* Edit and delete buttons */}
                    {editableCommentId === activity.id ? (
                      <div style={{ paddingTop: "10px" }}>
                        <IconButton onClick={() => handleSaveEdit(activity, editedCommentText)}>
                          <Save sx={{ width: "15px", height: "15px" }} />
                        </IconButton>
                        <IconButton onClick={handleCancelEdit}>
                          <Cancel sx={{ width: "15px", height: "15px" }} />
                        </IconButton>
                      </div>
                    ) : (
                      <div style={{ paddingTop: "20px", paddingLeft: "2rem" }}>
                        {/* <IconButton onClick={() => handleEdit(activity)}>
                          <Edit sx={{ width: "15px", height: "15px" }} />
                        </IconButton> */}
                        {editableCommentId !== activity.id && (
                          <IconButton onClick={() => handleDelete(ticketId, activity.id)}>
                            <Delete sx={{ 
                              width: "15px", 
                              height: "15px", 
                              color: isOwnComment ? "white" : "inherit" 
                            }} />
                          </IconButton>
                        )}
                      </div>
                    )}
                    {/* </Box>
              </Box>
            </Box> */}
                  </Paper>
                  
                  {isOwnComment && (
                    <Box sx={{ paddingLeft: 2 }}>
                      <MobiliAvatar
                        user={activity.text?.updatedByUser}
                        sx={{ width: 32, height: 32, fontSize: "0.8rem" }}
                      />
                    </Box>
                  )}
                </Box>
              </React.Fragment>
              );
            })
          )}
        </InfiniteScroll>
      </Box>

      <CommentInput
        newCommentText={newCommentText}
        setNewCommentText={setNewCommentText}
        onSubmit={handleSubmit}
        showEmojiPicker={showEmojiPicker}
        setShowEmojiPicker={setShowEmojiPicker}
        onEmojiSelect={handleEmojiSelect}
      />
    </Box>
  );
});

export default TicketComments;
